import type { NextPage, NextPageContext } from 'next';
import { useRouter } from 'next/router';
import React from 'react';

import { OrderListPageModule } from '@/components/modules/orders-page';
import { wrapperReduxToolkit } from '@/store';
import { setTechnicalError } from '@/store/reducers/settings/slice';
import { loadWithPopularProducts } from '@/store/utils/load-with-popular-products';
import { TOrder } from '@/types/orders';

import { FooterLk } from '@/widgets/footer';
import { Header } from '@/widgets/header';

import { HeadMeta } from '@/entities/meta-seo';
import { TRentProduct, PopularProducts } from '@/entities/product';

import { getActiveOrders, checkUserByIinWithValidate, TIinResponseOk } from '@/shared/api';
import { REGISTRATION_URL } from '@/shared/constants/paths';
import { useTranslate } from '@/shared/hooks/use-translate';
import { Heading } from '@/shared/ui/heading';
import { Layout } from '@/shared/ui/layout';
import { PageSection } from '@/shared/ui/page-section';

type TOrdersProps = {
    activeOrders: TOrder[];
    shouldRedirect: boolean;
    popularProducts: TRentProduct[];
};

const Page: NextPage<TOrdersProps> = ({ activeOrders, shouldRedirect, popularProducts }) => {
    const { t } = useTranslate();

    const router = useRouter();

    if (shouldRedirect) {
        router.push(REGISTRATION_URL);

        return <></>;
    }

    return (
        <>
            <HeadMeta title={t('Заказы')} />
            <Layout header={<Header />} footer={<FooterLk />}>
                <Layout.Content>
                    <Heading as="h1" marginB="30" marginBMobile="4">
                        {t('Заказы')}
                    </Heading>

                    <OrderListPageModule activeOrders={activeOrders} />

                    <PageSection title={t('Самые популярные 🔥')}>
                        <PopularProducts products={popularProducts} />
                    </PageSection>
                </Layout.Content>
            </Layout>
        </>
    );
};

Page.getInitialProps = wrapperReduxToolkit.getInitialPageProps(
    ({ dispatch, getState }) =>
        async (context: NextPageContext) => {
            const { locale } = context;

            // Загрузка стора при серверном и клиенском рединге, общих настроек
            const { user, popularProducts } = await loadWithPopularProducts({
                getState,
                dispatch,
                locale: locale as string,
                context,
            });

            if (user.auth.isUserLead) {
                if (context.res) {
                    context.res.writeHead(307, {
                        location: REGISTRATION_URL,
                    });

                    context.res.end();
                }

                return { shouldRedirect: true };
            }

            try {
                if (user?.data?.iin) {
                    const responseUserIin = await checkUserByIinWithValidate(user.data.iin as string);

                    const userResponse = responseUserIin as TIinResponseOk;

                    if (userResponse?.found === 'customer') {
                        const response = await getActiveOrders(context);

                        return {
                            activeOrders: response,
                            popularProducts,
                        };
                    }
                }
            } catch (err) {
                /* eslint-disable no-console */
                console.error('Order list getInitialProps error', err);

                // TODO костыль ARKZ-235
                dispatch(setTechnicalError({ error: err }));
            }

            return { popularProducts };
        },
);

export default Page;
